import { Grid } from "@tine/designsystem-ui-react";
import { observer } from "mobx-react-lite";
import { useState } from "react";
import type { JsonValue } from "type-fest";
import { Link as WouterLink } from "wouter";
import { APP_VERSION } from "../common/environment";
import MainColumn from "../components/common/MainColumn";
import PhoneLink from "../components/common/PhoneLink";
import StoreLogoComponent from "../components/StoreLogoComponent";
import contentStore from "../stores/cms/contentStore";
import uiStore from "../stores/uiStore";
import theme from "../themes/theme";
import ContentfulFooterLink, { type FooterLink } from "./ContentfulFooterLink";
import FooterDecorationImage from "./FooterDecorationImage";

const openingHourKey = (openingHour: JsonValue) =>
   openingHour &&
   typeof openingHour === "object" &&
   "key" in openingHour &&
   typeof openingHour.key === "string" && <div>{openingHour.key}</div>;

const openingHourValue = (openingHour: JsonValue) =>
   openingHour &&
   typeof openingHour === "object" &&
   "value" in openingHour &&
   typeof openingHour.value === "string" && <div>{openingHour.value}</div>;

const sectionClasses = "flex flex-col gap-4";

const Footer = () => {
   const { siteName } = theme;
   const { dataVersion } = uiStore;

   const footerContent = contentStore.footerContent.data;
   const currentYear = new Date().getFullYear();

   const columnCustomerServiceLabel = footerContent?.fields.columnCustomerServiceLabel as string;
   const columnTwoLabel = footerContent?.fields.column2label as string;
   const columnTwoLinks = footerContent?.fields.column2links as FooterLink[];
   const columnThreeLabel = footerContent?.fields.column3label as string;
   const columnThreeLinks = footerContent?.fields.column3links as FooterLink[];
   const columnFourLabel = footerContent?.fields.column4label as string;
   const columnFourLinks = footerContent?.fields.column4links as FooterLink[];

   const termsAndConditionLinks = footerContent?.fields.termsAndConditions as FooterLink[];

   const [showVersionInfo, setShowVersionInfo] = useState<boolean>(false);

   return (
      <MainColumn>
         <div className="flex flex-col w-full px-2 lg:px-0 print:hidden">
            <Grid cols={12} gap={{ base: 4, lg: 12 }} className="my-14">
               <Grid.Col span={{ base: 12, lg: 3, sm: 6 }} className={sectionClasses}>
                  <h3 className="text-xl">{columnCustomerServiceLabel}</h3>
                  <ul className="px-0">
                     {footerContent?.fields.openingHours &&
                        Array.isArray(footerContent.fields.openingHours) &&
                        footerContent.fields.openingHours?.map((openingHour, index) => (
                           <li key={`OpeningHourLine ${index + 1}`} className="flex gap-8 pb-4">
                              {openingHourKey(openingHour)}
                              {openingHourValue(openingHour)}
                           </li>
                        ))}
                     <li>
                        <PhoneLink
                           className="pb-4 underline text-headerFooterText hover:underline"
                           telephoneNumber={footerContent?.fields.contactNumber || ""}
                        />
                     </li>
                  </ul>
               </Grid.Col>

               {columnTwoLinks && (
                  <Grid.Col span={{ base: 12, lg: 3, sm: 6 }} className={sectionClasses}>
                     <h3 className="text-xl">{columnTwoLabel}</h3>
                     <ul className="px-0">
                        {columnTwoLinks?.map((link) => (
                           <li key={link.sys.id} className="pb-4">
                              <ContentfulFooterLink content={link} />
                           </li>
                        ))}
                     </ul>
                  </Grid.Col>
               )}

               {columnThreeLinks && (
                  <Grid.Col span={{ base: 12, lg: 3, sm: 6 }} className={sectionClasses}>
                     <h3 className="text-xl">{columnThreeLabel}</h3>
                     <ul className="px-0">
                        {columnThreeLinks?.map((link) => (
                           <li key={link.sys.id} className="pb-4">
                              <ContentfulFooterLink content={link} />
                           </li>
                        ))}
                     </ul>
                  </Grid.Col>
               )}

               {columnFourLinks && (
                  <Grid.Col span={{ base: 12, lg: 3, sm: 6 }} className={sectionClasses}>
                     <h3 className="text-xl">{columnFourLabel}</h3>
                     <ul className="px-0">
                        {columnFourLinks?.map((link) => (
                           <li key={link.sys.id} className="pb-4">
                              <ContentfulFooterLink content={link} />
                           </li>
                        ))}
                     </ul>
                  </Grid.Col>
               )}
            </Grid>
            <div className="flex flex-col w-full">
               <FooterDecorationImage />
               <div className="-mt-[1px] flex w-full flex-col justify-between gap-6 border-t border-solid pt-6 lg:flex-row lg:text-right">
                  <div className="flex flex-col gap-6 lg:flex-row">
                     {termsAndConditionLinks?.map((termsAndConditionLink) => (
                        <ContentfulFooterLink key={termsAndConditionLink.sys.id} content={termsAndConditionLink} />
                     ))}
                  </div>
                  <div className="relative">
                     <button type="button" className="cursor-default" onClick={() => setShowVersionInfo(!showVersionInfo)}>
                        &copy; {currentYear} {siteName}
                     </button>
                     {showVersionInfo && (
                        // biome-ignore lint/a11y/noStaticElementInteractions: should be a button
                        <span
                           className="absolute left-0 text-sm cursor-pointer top-8 text-nowrap lg:left-auto lg:right-0"
                           onClick={() => uiStore.checkDataVersion(true)}
                        >
                           d.{dataVersion} | b.{APP_VERSION}
                        </span>
                     )}
                  </div>
               </div>

               <div className="flex justify-center mt-12 mb-14">
                  <WouterLink to="/">
                     <StoreLogoComponent placement="footer" />
                  </WouterLink>
               </div>
            </div>
         </div>
      </MainColumn>
   );
};

export default observer(Footer);
